import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import {
  CommentDto,
  Country,
  CountryCollateral,
  CountryPartyType,
  CountryTreasuryParty
} from "../../../../../../api/Api";
import { CommentsSection } from "../../../../../../components/lifecycle/commentsSection";
import { LifecycleDialog } from "../../../../../../components/lifecycle/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useCountryAssignees } from "../../../../dataHooks/countryQueries";
import { countryAttachmentApi } from "../../../countryAttachmentApi";
import { useCountryLifecycle } from "../../useCountryLifecycle";
import { PartyRows } from "../../parties";
import {
  COLLATERAL_PARTY_TYPES,
  TREASURY_PARTY_TYPES,
  selectedPartyTypesWithin,
  syncPartyRows,
  updatePartyRow
} from "../../parties/utils";

type Props = {
  specificCountry: Country;
};

type FormData = {
  marginCallSystem: string;
  marginCallSystemAssignee: string;
  marginCallStatus: string;
  cashFlowForecast: string;
  cashFlowForecastAssignee: string;
  signOffNote: string;
  comments: CommentDto[];
};

const COLLATERAL_FORMS = [
  { value: "cash_deposit", label: "Cash deposit" },
  { value: "letter_of_credit", label: "Letter of credit" },
  { value: "bank_guarantee", label: "Bank guarantee" },
  { value: "parent_company_guarantee", label: "Parent company guarantee" },
  // Market Access says collateral is needed for the country, not which parties it lands on, so this
  // is how Treasury clears a party that turns out not to require any.
  { value: "not_needed", label: "Not needed" }
];

const emptyTreasuryParty = (
  partyType: CountryPartyType
): CountryTreasuryParty => ({
  partyType,
  agreementReview: "",
  agreementReviewAssignee: "",
  contactPerson: "",
  accountDetails: "",
  currency: ""
});

const emptyCollateral = (partyType: CountryPartyType): CountryCollateral => ({
  partyType,
  form: "",
  assignee: "",
  amount: "",
  currency: ""
});

/**
 * Two row collections, both derived from the parties Market Access marked needed. The agreement
 * review covers the banking parties, and collateral covers the four parties that can actually hold
 * it, once Market Access has said collateral is needed at all. A licence authority and a law firm
 * are excluded from collateral on the way in rather than left to be dismissed on every country.
 * Margin call and cash flow forecast are country-wide.
 */
export const Treasury: FC<Props> = ({ specificCountry }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const lifecycle = specificCountry.treasuryLifecycle;
  const marketAccess = specificCountry.marketAccessLifecycle;

  const [formData, setFormData] = useState<FormData>({
    marginCallSystem: lifecycle?.marginCallSystem || "",
    marginCallSystemAssignee: lifecycle?.marginCallSystemAssignee || "",
    marginCallStatus: lifecycle?.marginCallStatus || "",
    cashFlowForecast: lifecycle?.cashFlowForecast || "",
    cashFlowForecastAssignee: lifecycle?.cashFlowForecastAssignee || "",
    signOffNote: lifecycle?.signOffNote || "",
    comments: lifecycle?.comments || []
  });

  // Treasury only ever deals with the bank and the clearing bank, so its own rows are the needed
  // parties narrowed to those two.
  const [parties, setParties] = useState<CountryTreasuryParty[]>(() =>
    syncPartyRows(
      lifecycle?.parties,
      selectedPartyTypesWithin(marketAccess, TREASURY_PARTY_TYPES),
      emptyTreasuryParty
    )
  );

  const [collaterals, setCollaterals] = useState<CountryCollateral[]>(() =>
    syncPartyRows(
      lifecycle?.collaterals,
      marketAccess?.collateral === "needed"
        ? selectedPartyTypesWithin(marketAccess, COLLATERAL_PARTY_TYPES)
        : [],
      emptyCollateral
    )
  );

  const handleClose = () => navigate(`/countries/country/${params.id}`);

  const extraSlice = () => ({ parties, collaterals });

  const {
    handleSave,
    handleComplete,
    isSaving,
    progressOverride,
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment
  } = useCountryLifecycle({
    country: specificCountry,
    lifecycle: "treasuryLifecycle",
    formData,
    setFormData,
    onClose: handleClose
  });

  const treasuryAssignees = useCountryAssignees("treasury");

  const handleChange = (field: keyof FormData, value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const open = location.pathname.includes("treasury");

  const marginCallNeeded = formData.marginCallSystem === "needed";

  const canComplete = Boolean(
    parties.every((party) => Boolean(party.agreementReview)) &&
      collaterals.every((collateral) => Boolean(collateral.form)) &&
      formData.marginCallSystem &&
      (!marginCallNeeded || formData.marginCallStatus) &&
      formData.cashFlowForecast
  );

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Treasury"
      progressOverride={progressOverride}
      maxWidth="md"
      attachmentApi={countryAttachmentApi}
      actions={{
        onClose: handleClose,
        onSave: () => handleSave(extraSlice()),
        onComplete: () => handleComplete(extraSlice()),
        canComplete,
        isSaving
      }}
    >
      <Box sx={{ paddingTop: 2 }} gap={2} display="flex" flexDirection="column">
        <PartyRows
          title="Agreement review, collateral and liquidity terms"
          rows={parties}
          marketAccess={marketAccess}
          emptyMessage="Market Access has not marked a bank or clearing bank as needed."
          renderRow={(party) => {
            const partyType = party.partyType as CountryPartyType;
            const update = (patch: Partial<CountryTreasuryParty>) =>
              setParties((prev) => updatePartyRow(prev, partyType, patch));

            return (
              <>
                <Box display="flex" gap={2}>
                  <FormControl size="medium" required sx={{ flex: 1 }}>
                    <InputLabel>Agreement review</InputLabel>
                    <Select
                      label="Agreement review"
                      value={party.agreementReview || ""}
                      onChange={(e) =>
                        update({ agreementReview: e.target.value })
                      }
                    >
                      <MenuItem value="initiated">Initiated</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </Select>
                  </FormControl>

                  <Autocomplete
                    sx={{ flex: 1 }}
                    options={treasuryAssignees.data || []}
                    getOptionLabel={(option) =>
                      `${option.name} (${option.email})`
                    }
                    value={
                      treasuryAssignees.data?.find(
                        (a) => a.email === party.agreementReviewAssignee
                      ) || null
                    }
                    onChange={(_, newValue) =>
                      update({ agreementReviewAssignee: newValue?.email || "" })
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Assignee" size="medium" />
                    )}
                  />
                </Box>

                <TextField
                  label="Contact person"
                  value={party.contactPerson || ""}
                  onChange={(e) => update({ contactPerson: e.target.value })}
                  fullWidth
                />

                <Box display="flex" gap={2}>
                  <TextField
                    sx={{ flex: 2 }}
                    label="Account details"
                    value={party.accountDetails || ""}
                    onChange={(e) => update({ accountDetails: e.target.value })}
                  />
                  <TextField
                    sx={{ flex: 1 }}
                    label="Currency"
                    value={party.currency || ""}
                    onChange={(e) => update({ currency: e.target.value })}
                  />
                </Box>
              </>
            );
          }}
        />

        <Divider />

        <PartyRows
          title="Collateral"
          rows={collaterals}
          marketAccess={marketAccess}
          emptyMessage="Market Access has not marked collateral as needed."
          renderRow={(collateral) => {
            const partyType = collateral.partyType as CountryPartyType;
            const update = (patch: Partial<CountryCollateral>) =>
              setCollaterals((prev) => updatePartyRow(prev, partyType, patch));

            return (
              <>
                <Box display="flex" gap={2}>
                  <FormControl size="medium" required sx={{ flex: 1 }}>
                    <InputLabel>Collateral form</InputLabel>
                    <Select
                      label="Collateral form"
                      value={collateral.form || ""}
                      onChange={(e) => update({ form: e.target.value })}
                    >
                      {COLLATERAL_FORMS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Autocomplete
                    sx={{ flex: 1 }}
                    options={treasuryAssignees.data || []}
                    getOptionLabel={(option) =>
                      `${option.name} (${option.email})`
                    }
                    value={
                      treasuryAssignees.data?.find(
                        (a) => a.email === collateral.assignee
                      ) || null
                    }
                    onChange={(_, newValue) =>
                      update({ assignee: newValue?.email || "" })
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Assignee" size="medium" />
                    )}
                  />
                </Box>

                <Box display="flex" gap={2}>
                  <TextField
                    sx={{ flex: 2 }}
                    label="Amount"
                    value={collateral.amount || ""}
                    onChange={(e) => update({ amount: e.target.value })}
                  />
                  <TextField
                    sx={{ flex: 1 }}
                    label="Currency"
                    value={collateral.currency || ""}
                    onChange={(e) => update({ currency: e.target.value })}
                  />
                </Box>
              </>
            );
          }}
        />

        <Divider />

        <Box display="flex" gap={2}>
          <FormControl size="medium" required sx={{ flex: 1 }}>
            <InputLabel>Margin call system</InputLabel>
            <Select
              label="Margin call system"
              value={formData.marginCallSystem}
              onChange={(e) => handleChange("marginCallSystem", e.target.value)}
            >
              <MenuItem value="needed">Needed</MenuItem>
              <MenuItem value="not_needed">Not needed</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={treasuryAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              treasuryAssignees.data?.find(
                (a) => a.email === formData.marginCallSystemAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("marginCallSystemAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Margin call system assignee"
                size="medium"
              />
            )}
          />
        </Box>

        {marginCallNeeded && (
          <FormControl size="medium" required>
            <InputLabel>Margin call status</InputLabel>
            <Select
              label="Margin call status"
              value={formData.marginCallStatus}
              onChange={(e) => handleChange("marginCallStatus", e.target.value)}
            >
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="not_completed">Not completed</MenuItem>
              <MenuItem value="denied">Denied</MenuItem>
            </Select>
          </FormControl>
        )}

        <Box display="flex" gap={2}>
          <FormControl size="medium" required sx={{ flex: 1 }}>
            <InputLabel>Cash flow forecast</InputLabel>
            <Select
              label="Cash flow forecast"
              value={formData.cashFlowForecast}
              onChange={(e) => handleChange("cashFlowForecast", e.target.value)}
            >
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="not_completed">Not completed</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={treasuryAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              treasuryAssignees.data?.find(
                (a) => a.email === formData.cashFlowForecastAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("cashFlowForecastAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Cash flow forecast assignee"
                size="medium"
              />
            )}
          />
        </Box>

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) => handleChange("signOffNote", value)}
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />
      </Box>
    </LifecycleDialog>
  );
};
